import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { BookOpen } from 'lucide-react';
import { BackButton } from '@/components/common/BackButton';
import { ErrorState } from '@/components/common/ErrorState';
import { PageTitle } from '@/components/layout/PageTitle';
import { PageToolbar } from '@/components/layout/PageToolbar';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { SubmitterDetails } from '@/components/common/SubmitterDetails';
import { cn } from '@/lib/utils';
import { teacherDetailQueryOptions } from '../api/teacherSubmissions.api';
import {
  TeacherDetailsTable,
  TeacherDetailsTableSkeleton,
} from '../components/TeacherDetailsTable';
import {
  TeacherExpertiseGrid,
  TeacherExpertiseGridSkeleton,
} from '../components/TeacherExpertiseGrid';
import { TeacherInfoCard, TeacherInfoCardSkeleton } from '../components/TeacherInfoCard';
import { TeacherProfileCard, TeacherProfileCardSkeleton } from '../components/TeacherProfileCard';
import { useLastViewedTeacherStore } from '../store/lastViewedTeacher.store';
import type { TeacherDetail } from '../types/teacherSubmission.types';

const TEACHER_DETAIL_TITLE = 'Teacher details';
const PROFILE_COLUMNS_CLASS = 'grid items-start gap-4 @2xl:grid-cols-[18rem_minmax(0,1fr)]';
const PROFILE_CARD_STICKY_CLASS = 'lg:sticky lg:top-0';
const DETAIL_CARDS_GRID_CLASS = 'grid min-w-0 content-start items-start gap-4';
const SKELETON_REVEAL_CLASS =
  'motion-safe:animate-in fade-in-0 duration-200 delay-150 ease-out fill-mode-both';
const CARD_REVEAL_CLASS =
  'motion-safe:animate-in fade-in-0 slide-in-from-bottom-2 duration-300 ease-out fill-mode-both';
const CARD_REVEAL_DELAY_CLASS = {
  profile: 'delay-0',
  details: 'delay-50',
  expertise: 'delay-100',
} as const;

type CardRevealStep = keyof typeof CARD_REVEAL_DELAY_CLASS;

const getCardRevealClass = (step: CardRevealStep): string =>
  cn(CARD_REVEAL_CLASS, CARD_REVEAL_DELAY_CLASS[step]);
const EXPERTISE_ROW_COUNT = 3;

interface TeacherDetailColumnsProps {
  teacher: TeacherDetail;
  onShowOnlyThisInstitute: () => void;
}

function TeacherDetailColumns({ teacher, onShowOnlyThisInstitute }: TeacherDetailColumnsProps) {
  return (
    <div className={PROFILE_COLUMNS_CLASS}>
      <TeacherProfileCard
        teacher={teacher}
        onShowOnlyThisInstitute={onShowOnlyThisInstitute}
        className={cn(PROFILE_CARD_STICKY_CLASS, getCardRevealClass('profile'))}
      />

      <div className={DETAIL_CARDS_GRID_CLASS}>
        <TeacherDetailsTable teacher={teacher} className={getCardRevealClass('details')} />

        <TeacherInfoCard
          icon={BookOpen}
          title="Classes and subjects"
          bodyClassName="p-0"
          className={getCardRevealClass('expertise')}
        >
          <TeacherExpertiseGrid teacher={teacher} />
        </TeacherInfoCard>
      </div>
    </div>
  );
}

function TeacherDetailSkeleton() {
  return (
    <div className={cn(PROFILE_COLUMNS_CLASS, SKELETON_REVEAL_CLASS)} aria-busy>
      <TeacherProfileCardSkeleton />

      <div className={DETAIL_CARDS_GRID_CLASS}>
        <TeacherDetailsTableSkeleton />
        <TeacherInfoCardSkeleton bodyClassName="p-0">
          <TeacherExpertiseGridSkeleton rowCount={EXPERTISE_ROW_COUNT} />
        </TeacherInfoCardSkeleton>
      </div>
    </div>
  );
}

interface TeacherDetailBodyProps {
  teacherId: number;
  onShowOnlyThisInstitute: (instituteId: number) => void;
}

function TeacherDetailBody({ teacherId, onShowOnlyThisInstitute }: TeacherDetailBodyProps) {
  const {
    data: teacher,
    error,
    refetch,
    isFetching,
  } = useQuery(teacherDetailQueryOptions(teacherId));

  if (error) {
    return (
      <ErrorState
        error={error}
        fallbackTitle="Could not load this teacher"
        onRetry={() => void refetch()}
        isRetrying={isFetching}
      />
    );
  }

  if (!teacher) return <TeacherDetailSkeleton />;

  return (
    <TeacherDetailColumns
      teacher={teacher}
      onShowOnlyThisInstitute={() => onShowOnlyThisInstitute(teacher.institute.id)}
    />
  );
}

interface TeacherSubmissionDetailPageProps {
  teacherId: number;
}

export function TeacherSubmissionDetailPage({ teacherId }: TeacherSubmissionDetailPageProps) {
  const router = useRouter();
  const navigate = useNavigate();
  const { data: teacher } = useQuery(teacherDetailQueryOptions(teacherId));
  const setLastViewedTeacherId = useLastViewedTeacherStore((state) => state.setLastViewedTeacherId);

  useEffect(() => {
    setLastViewedTeacherId(teacherId);
  }, [teacherId, setLastViewedTeacherId]);

  function handleBack() {
    if (router.history.canGoBack()) {
      router.history.back();
      return;
    }
    void navigate({ to: '/settings/teacher-submissions' });
  }

  function handleShowOnlyThisInstitute(instituteId: number) {
    void navigate({ to: '/settings/teacher-submissions', search: { instituteId } });
  }

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <header className="flex flex-col gap-2">
        <PageTitle leading={<BackButton onClick={handleBack} />}>{TEACHER_DETAIL_TITLE}</PageTitle>
        <PageToolbar currentTitle={TEACHER_DETAIL_TITLE} />
        {teacher?.appUpdatedByName && teacher.appUpdatedAt && (
          <SubmitterDetails
            name={teacher.appUpdatedByName}
            hrEmpId={teacher.appUpdatedByHrEmpId}
            amName={teacher.appUpdatedByAmName}
            submittedAt={teacher.appUpdatedAt}
          />
        )}
      </header>

      <div className="@container lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-contain">
        <TeacherDetailBody
          teacherId={teacherId}
          onShowOnlyThisInstitute={handleShowOnlyThisInstitute}
        />
      </div>
    </div>
  );
}
